/**
 * Web Bluetooth driver for the R2 UHF handheld RFID reader (Nordic UART).
 *
 * The reader streams application-protocol frames (V2.08) over the Nordic
 * UART Service. Pulling the physical trigger starts/stops inventory on the
 * device itself, so no commands are strictly required — we just parse the
 * tag-report frames (cmd 0x81 single / 0x83 continuous) and re-emit EPC
 * bursts through the SAME `pda-rfid-scan` window event the PDA wrapper
 * used, so every existing scan screen keeps working unchanged.
 *
 * Frame: [hdr 2B: C8 8C][len 2B MSB (total frame len)][cmd 1B][data NB][bcc 1B][tail 0D 0A]
 * BCC = XOR of bytes from the length field through the data (hdr/tail excluded).
 * Tag report data: [PC 2B][EPC nB][RSSI 2B][ANT 1B] — EPC length derived from
 * the frame length (robust even when PC bits are unusual).
 */

const NUS_SERVICE = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
const NUS_RX = "6e400002-b5a3-f393-e0a9-e50e24dcca9e"; // host → reader (write)
const NUS_TX = "6e400003-b5a3-f393-e0a9-e50e24dcca9e"; // reader → host (notify)

const FLUSH_MS = 600; // burst ends when no tag arrives for this long
const IDLE_STOP_MS = 30000; // safety: auto-stop after 30s without a NEW tag

const state = {
  device: null,
  rxChar: null,
  connected: false,
  connecting: false,
  scanning: false,
  buffer: new Uint8Array(0),
  pending: new Set(),
  flushTimer: null,
  pollTimer: null,
  lastRxAt: 0,
  sessionTotal: 0,
};

const emit = (type, detail) => window.dispatchEvent(new CustomEvent(type, { detail }));
const emitStatus = () =>
  emit("ble-reader-status", {
    connected: state.connected,
    connecting: state.connecting,
    scanning: state.scanning,
    name: state.device?.name || null,
    pending: state.pending.size,
    sessionTotal: state.sessionTotal,
  });

export const isBleSupported = () =>
  typeof navigator !== "undefined" && !!navigator.bluetooth;

export const getBleState = () => ({
  connected: state.connected,
  connecting: state.connecting,
  scanning: state.scanning,
  name: state.device?.name || null,
  pending: state.pending.size,
  sessionTotal: state.sessionTotal,
});

/* ---------- frame parsing (pure, exported for tests) ---------- */

export function parseFrames(buffer) {
  // Returns { epcs, keys, pollReplies, remain, rest }
  const epcs = [];
  const keys = [];
  let pollReplies = 0;
  let remain = 0;
  let buf = buffer;
  for (;;) {
    // find a header (C8 8C primary, A5 5A tolerated)
    let idx = -1;
    for (let i = 0; i + 1 < buf.length; i++) {
      if ((buf[i] === 0xc8 && buf[i + 1] === 0x8c) || (buf[i] === 0xa5 && buf[i + 1] === 0x5a)) { idx = i; break; }
    }
    if (idx < 0) return { epcs, keys, pollReplies, remain, rest: buf.length > 1 ? buf.slice(buf.length - 1) : buf };
    if (idx > 0) buf = buf.slice(idx);
    if (buf.length < 5) return { epcs, keys, pollReplies, remain, rest: buf };
    const len = (buf[2] << 8) | buf[3];
    if (len < 8 || len > 600) { buf = buf.slice(2); continue; } // garbage length — resync
    if (buf.length < len) return { epcs, keys, pollReplies, remain, rest: buf }; // wait for more bytes
    const frame = buf.slice(0, len);
    buf = buf.slice(len);
    // tail sanity (tolerate missing tail — some firmwares omit it)
    const cmd = frame[4];
    if (cmd === 0xe6) {
      // trigger/key event: data[0] = 1 pressed, 0 released
      keys.push(frame[5]);
    } else if (cmd === 0xe1) {
      // Buffered-tag reply to our 0xE0 fetch — native-speed path. Payload:
      // [remain 2B][count 1B] then count × ([tagLen 1B][PC 2B + EPC + RSSI 2B]).
      // tagLen covers PC+EPC+RSSI, so strip BOTH the 2-byte PC prefix and the
      // 2-byte RSSI suffix to get the plain EPC the inventory stored (else
      // every EPC gets a "3000" prefix / "FDxx" suffix and fails to match).
      pollReplies += 1;
      const data = frame.slice(5, len - 3);
      if (data.length >= 3) {
        remain = (data[0] << 8) | data[1]; // tags still buffered in reader
        const count = data[2];
        let p = 3;
        for (let n = 0; n < count && p < data.length; n++) {
          const tagLen = data[p]; p += 1;
          if (p + tagLen > data.length || tagLen < 8) break;
          const epcBytes = data.slice(p + 2, p + tagLen - 2); // skip PC(2), drop RSSI(2)
          p += tagLen;
          if (epcBytes.length >= 4) {
            const hex = Array.from(epcBytes, (b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
            epcs.push(hex);
          }
        }
      }
    } else if (cmd === 0x81 || cmd === 0x83) {
      if (cmd === 0x81) pollReplies += 1; // counts tag AND no-tag (F1) replies
      // data = frame[5 .. len-3-1]; layout PC(2) EPC(n) RSSI(2) ANT(1)
      const data = frame.slice(5, len - 3);
      if (data.length >= 7) {
        // Prefer PC-derived EPC length; fall back to frame-derived length.
        const pcWords = (data[0] >> 3) & 0x1f;
        let epcLen = pcWords * 2;
        const frameDerived = data.length - 5; // minus PC(2) RSSI(2) ANT(1)
        if (epcLen < 4 || epcLen > frameDerived) epcLen = frameDerived;
        const epcBytes = data.slice(2, 2 + epcLen);
        if (epcBytes.length >= 4) {
          const hex = Array.from(epcBytes, (b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
          epcs.push(hex);
        }
      }
    }
  }
}

/* ---------- streaming ---------- */

function onData(event) {
  const v = event.target.value;
  // respect byteOffset/byteLength — some stacks (Bluefy) hand us a DataView
  // over a larger shared buffer; reading .buffer directly yields garbage.
  const chunk = new Uint8Array(v.buffer, v.byteOffset || 0, v.byteLength);
  const hexStr = Array.from(chunk, (b) => b.toString(16).padStart(2, "0")).join(" ");
  state.lastRxAt = Date.now();
  pushLog(`rx ${hexStr}`);
  emit("ble-reader-raw", { hex: hexStr });
  const merged = new Uint8Array(state.buffer.length + chunk.length);
  merged.set(state.buffer, 0);
  merged.set(chunk, state.buffer.length);
  const { epcs, keys, pollReplies, remain, rest } = parseFrames(merged);
  state.buffer = rest;
  // Self-clocking 0xE0 drain: as soon as a buffer reply comes back, request
  // the next one — this rides the BLE connection interval instead of a fixed
  // 30ms timer (the "rhythmic beep" = under-driven buffer). If the reply says
  // tags REMAIN in the buffer, fetch again immediately to empty it in one go.
  if (state.scanning && pollReplies > 0) {
    writeCmd(pollFrame());
    if (remain > 0) writeCmd(pollFrame());
  }
  // Trigger = HOLD to scan, RELEASE to stop (standard RFID-gun feel, and the
  // only reliable model: pull sends E6 01 repeats ~every 0.5-1s while held;
  // releasing stops the repeats, and a watchdog stops the scan 1.2s later).
  for (const k of keys) {
    pushLog(`trigger event ${k}`);
    if (state.suppressTrigger) continue; // diagnostics running — don't react
    if (Date.now() - (state.connectedAt || 0) < 1500) continue;
    if (k === 1) {
      state.lastTriggerSeenAt = Date.now();
      if (!state.scanning && Date.now() - (state.lastStopAt || 0) > 400) {
        state.scanSource = "trigger";
        startInventory();
      }
    } else if (k === 0 && state.scanning) {
      stopInventory(); // explicit release event, if the firmware sends it
    }
  }
  if (epcs.length) {
    state.lastTagAt = Date.now();
    // Leftover inventory from a previous session still streaming → quiet it
    // once (we are not scanning and these tags were not asked for).
    if (!state.scanning && !state.quietSent) {
      state.quietSent = true;
      writeCmd(buildFrame(0x8c, [], [0xa5, 0x5a]));
      return;
    }
    let anyNew = false;
    for (const e of epcs) {
      if (!state.pending.has(e)) {
        state.pending.add(e);
        state.sessionTotal += 1;
        anyNew = true;
      }
    }
    if (anyNew) state.lastNewTagAt = Date.now();
    emitStatus();
    // Fixed-cadence flush: with a continuous stream the old "reset the timer
    // on every chunk" pattern NEVER fired — tags kept arriving. Schedule only
    // when no flush is pending, so bursts surface every FLUSH_MS regardless.
    if (!state.flushTimer) state.flushTimer = setTimeout(flushBurst, FLUSH_MS);
  }
}

function flushBurst() {
  state.flushTimer = null;
  if (state.pending.size === 0) return;
  const epcs = [...state.pending];
  state.pending.clear();
  emit("pda-rfid-scan", { epcs }); // same event the PDA wrapper fired
  emitStatus();
}

/* ---------- commands ----------
 * When a BLE host is connected the R2 switches to slave/command mode and the
 * physical trigger stops driving inventory — the host must send start/stop.
 * The protocol doc shows command frames with the C8 8C header, but some
 * firmwares expect A5 5A, so we send both variants (duplicates are harmless).
 */

function buildFrame(cmd, data = [], header = [0xc8, 0x8c]) {
  const len = 2 + 2 + 1 + data.length + 1 + 2;
  const body = [(len >> 8) & 0xff, len & 0xff, cmd, ...data];
  let bcc = 0;
  for (const b of body) bcc ^= b;
  return new Uint8Array([...header, ...body, bcc, 0x0d, 0x0a]);
}

let writeChain = Promise.resolve();

function pollFrame() {
  // getLabMessage (0xE0) — fetch tags the reader buffered during continuous
  // inventory. This is how the official SDK reads at native speed: fire 0x82
  // once, then spam 0xE0 every ~20ms; the reader answers with 0xE1 frames
  // that each carry MANY tags. (Not the slow 0x80 single-poll we used.)
  return buildFrame(0xe0, [], [0xc8, 0x8c]);
}

async function writeCmd(bytes) {
  if (!state.rxChar) return;
  // Serialise GATT writes — iOS/Bluefy throws "operation in progress" when
  // a write starts before the previous one resolves, silently killing polls.
  writeChain = writeChain.then(async () => {
    if (!state.rxChar) return;
    try {
      if (state.rxChar.writeValueWithoutResponse) await state.rxChar.writeValueWithoutResponse(bytes);
      else await state.rxChar.writeValue(bytes);
      emit("ble-reader-raw", { hex: "TX> " + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(" ") });
    } catch (e) {
      pushLog(`write failed: ${e?.message}`);
      emit("ble-reader-raw", { hex: `TX-ERR ${e?.message || "write failed"}` });
    }
  });
  return writeChain;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* ---------- adjustable TX power (dBm, persisted) ---------- */

let txPowerDbm = (() => {
  const v = Number(typeof localStorage !== "undefined" && localStorage.getItem("ble_tx_power"));
  return v >= 5 && v <= 30 ? v : 30;
})();

export const getTxPower = () => txPowerDbm;

function powerFrame() {
  const val = txPowerDbm * 100; // protocol unit = dBm × 100
  const hi = (val >> 8) & 0xff, lo = val & 0xff;
  // This firmware uses the "read power" field too — set BOTH read and write
  // (the doc calls read reserved, but read=0 made the unit scan nothing).
  return buildFrame(0x10, [0x00, 0x01, hi, lo, hi, lo], [0xa5, 0x5a]);
}

export async function setTxPower(dbm) {
  txPowerDbm = Math.min(30, Math.max(5, Math.round(dbm)));
  try { localStorage.setItem("ble_tx_power", String(txPowerDbm)); } catch (e) { /* noop */ }
  pushLog(`tx power set to ${txPowerDbm} dBm`);
  if (state.connected && state.scanning) {
    // Reader ignores commands during continuous inventory → stop, set, restart.
    await stopInventory();
    await sleep(150);
    await startInventory();
  }
  emitStatus();
}

export async function startInventory() {
  if (state.scanning) return; // already running — never stack poll loops
  if (state.pollTimer) { clearInterval(state.pollTimer); state.pollTimer = null; }
  state.scanning = true;
  emitStatus();
  state.lastNewTagAt = Date.now();
  state.lastTagAt = 0;
  state.scanStartedAt = Date.now();
  // Official-SDK native-speed recipe (reverse-engineered from Chainway iOS
  // BluetoothUtil.m): fire continuous inventory 0x82 ONCE, then poll the
  // on-device buffer with 0xE0 every 30ms — the reader answers with 0xE1
  // frames that each carry MANY tags, hitting >200 tags/s.
  // Keep the CW-off + fixed-freq + power prep that made reads reliable.
  await writeCmd(buildFrame(0x24, [0x00], [0xa5, 0x5a])); // CW off
  await sleep(120);
  await writeCmd(buildFrame(0x14, [0x01, 0x0e, 0x12, 0x8a], [0xa5, 0x5a])); // fixed 922.25MHz
  await sleep(120);
  await writeCmd(powerFrame());
  await sleep(120);
  // TagFocus ON (0x60 01): a tag that's been read stays quiet (Session-1 B
  // state held long), so the reader keeps finding NEW tags instead of
  // re-reading the same ones — fixes "fast at first, then slows to a crawl".
  await writeCmd(buildFrame(0x60, [0x01, 0x00], [0xa5, 0x5a]));
  await sleep(120);
  // Start continuous inventory (0x82, infinite). The reader now scans
  // internally at full speed and buffers tags for 0xE0 to drain.
  await writeCmd(buildFrame(0x82, [0x00, 0x00], [0xc8, 0x8c]));
  await sleep(60);
  state.lastKeepAlive = Date.now();
  state.lastTriggerSeenAt = Date.now();
  const id = setInterval(() => {
    if (!state.scanning || !state.connected) { clearInterval(id); if (state.pollTimer === id) state.pollTimer = null; return; }
    // RELEASE detection: a trigger-started scan stops once the held-trigger
    // E6 01 repeats cease for 1.2s (operator let go). This is the reliable
    // stop path. Web-button scans ignore this and stop via STOP only.
    if (state.scanSource === "trigger" && Date.now() - (state.lastTriggerSeenAt || 0) > 1200) {
      pushLog("trigger released — stop");
      stopInventory();
      return;
    }
    if (Date.now() - state.lastNewTagAt > IDLE_STOP_MS) {
      pushLog("idle 30s — auto stop");
      stopInventory(); // also clears this interval via scanning=false
      return;
    }
    // Keep-alive: re-arm 0x82 only when NO new tag for 3s (inventory idle) —
    // never mid-stream, so high-speed reading is never interrupted.
    if (Date.now() - state.lastNewTagAt > 3000 && Date.now() - (state.lastKeepAlive || 0) > 3000) {
      state.lastKeepAlive = Date.now();
      writeCmd(buildFrame(0x82, [0x00, 0x00], [0xc8, 0x8c]));
    }
    // Watchdog only — the self-clocking drain in onData does the real work;
    // this just restarts the chain if a reply was dropped.
    if (Date.now() - state.lastRxAt > 150) writeCmd(pollFrame());
  }, 120);
  state.pollTimer = id;
}

export async function stopInventory() {
  state.scanning = false; // flips first — every live poll loop self-terminates
  state.lastStopAt = Date.now();
  if (state.nextPollTimer) { clearTimeout(state.nextPollTimer); state.nextPollTimer = null; }
  emitStatus(); // always tell the UI — flushBurst skips empty bursts
  if (state.pollTimer) { clearInterval(state.pollTimer); state.pollTimer = null; }
  // Stop continuous inventory (0x8C), then one more for good measure.
  await writeCmd(buildFrame(0x8c, [], [0xc8, 0x8c]));
  await sleep(100);
  await writeCmd(buildFrame(0x8c, [], [0xa5, 0x5a]));
  if (state.flushTimer) clearTimeout(state.flushTimer);
  flushBurst();
}

export async function runDiagnostics() {
  // Query every relevant setting, then try one long single poll (1s window).
  // Replies land in the raw log — one screenshot shows the full device state.
  const q = [
    [0x00, "hw ver"], [0x02, "fw ver"], [0x12, "get power"],
    [0x16, "get hopping"], [0x22, "get gen2"], [0x26, "get CW"],
    [0x2a, "get antenna"], [0x2e, "get region"], [0x34, "get temp"],
    [0x54, "get RF link"], [0x5e, "get FastID"], [0x62, "get TagFocus"],
  ];
  for (const [cmd, label] of q) {
    emit("ble-reader-raw", { hex: `-- ${label} (0x${cmd.toString(16)})` });
    await writeCmd(buildFrame(cmd, [], [0xa5, 0x5a]));
    await sleep(250);
  }
  emit("ble-reader-raw", { hex: "-- single poll, 1s window" });
  await writeCmd(buildFrame(0x80, [0x03, 0xe8], [0xa5, 0x5a]));
  await sleep(1300);
  emit("ble-reader-raw", { hex: "-- diagnostics done" });
}

export async function testContinuous() {
  // Try 4 continuous-inventory variants, listening 4s each — whichever makes
  // the reader STREAM 0x83 tag frames is the fast native-speed mode.
  state.suppressTrigger = true;
  try {
  // CW off + fixed AU frequency first — previous runs had CW ON, which
  // silently breaks every inventory mode.
  await writeCmd(buildFrame(0x24, [0x00], [0xa5, 0x5a]));
  await sleep(150);
  await writeCmd(buildFrame(0x14, [0x01, 0x0e, 0x12, 0x8a], [0xa5, 0x5a]));
  await sleep(150);
  const variants = [
    ["A5 5A num=10000", [0xa5, 0x5a], [0x27, 0x10]],
    ["A5 5A num=0 (infinite)", [0xa5, 0x5a], [0x00, 0x00]],
    ["C8 8C num=10000", [0xc8, 0x8c], [0x27, 0x10]],
    ["C8 8C num=0 (infinite)", [0xc8, 0x8c], [0x00, 0x00]],
  ];
  await writeCmd(powerFrame());
  await sleep(200);
  for (const [label, header, num] of variants) {
    emit("ble-reader-raw", { hex: `== trying 0x82 ${label} — listen 4s ==` });
    await writeCmd(buildFrame(0x82, num, header));
    await sleep(4000);
    await writeCmd(buildFrame(0x8c, [], [0xa5, 0x5a])); // stop before next
    await writeCmd(buildFrame(0x8c, [], [0xc8, 0x8c]));
    await sleep(400);
  }
  emit("ble-reader-raw", { hex: "== continuous test done ==" });
  } finally { state.suppressTrigger = false; }
}

export async function factoryReset() {
  // 0x74 — clears any stray filter/antenna/power setting; the unit reboots
  // itself afterwards (BLE will disconnect, reconnect manually).
  pushLog("factory reset sent");
  await writeCmd(buildFrame(0x74, [], [0xa5, 0x5a]));
  await sleep(150);
  await writeCmd(buildFrame(0x74, [], [0xc8, 0x8c]));
}

export async function toggleScan() {
  if (state.scanning) await stopInventory();
  else { state.scanSource = "web"; await startInventory(); }
}

/* ---------- debug ring log (window.__bleDebug) ---------- */
const debugLog = [];
function pushLog(msg) {
  debugLog.push(`${new Date().toISOString().slice(11, 19)} ${msg}`);
  if (debugLog.length > 200) debugLog.shift();
  if (typeof window !== "undefined") window.__bleDebug = debugLog;
}

/* ---------- connection ---------- */

function onDisconnected() {
  state.connected = false;
  state.scanning = false;
  if (state.pollTimer) { clearInterval(state.pollTimer); state.pollTimer = null; }
  state.rxChar = null;
  state.buffer = new Uint8Array(0);
  if (state.flushTimer) { clearTimeout(state.flushTimer); flushBurst(); }
  emitStatus();
}

export async function connectReader() {
  if (!isBleSupported()) throw new Error("This browser doesn't support Bluetooth (use Chrome / Edge)");
  state.connecting = true;
  emitStatus();
  try {
    // acceptAllDevices: some units advertise without the NUS UUID or use a
    // custom name, and Bluefy (iOS) matches filters less reliably — show
    // every nearby BLE device and let the operator pick the reader.
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [NUS_SERVICE],
    });
    device.addEventListener("gattserverdisconnected", onDisconnected);
    const server = await device.gatt.connect();
    let svc;
    try {
      svc = await server.getPrimaryService(NUS_SERVICE);
    } catch (e) {
      try { device.gatt.disconnect(); } catch (e2) { /* noop */ }
      throw new Error(`"${device.name || "This device"}" doesn't expose the UART service — pick the RFID reader (usually "Nordic_UART_CW")`);
    }
    const tx = await svc.getCharacteristic(NUS_TX);
    try { state.rxChar = await svc.getCharacteristic(NUS_RX); } catch (e) { state.rxChar = null; }
    await tx.startNotifications();
    tx.addEventListener("characteristicvaluechanged", onData);
    state.device = device;
    state.connected = true;
    state.sessionTotal = 0;
    state.scanning = false;
    state.quietSent = false;
    state.connectedAt = Date.now();
    // PASSIVE connect — send nothing (TX on connect makes the unit beep).
    // If a leftover inventory is still streaming, onData sends one stop.
    return device.name || "RFID reader";
  } finally {
    state.connecting = false;
    emitStatus();
  }
}

export function disconnectReader() {
  try { state.device?.gatt?.disconnect(); } catch (e) { /* noop */ }
  onDisconnected();
}
